import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Send, MessageCircle, Facebook, Instagram, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { siteInfo } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast({ title: "Messaggio inviato!", description: res.data.message });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Errore", description: err.response?.data?.detail || "Impossibile inviare il messaggio. Riprova.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    { icon: MapPin, title: "Indirizzo", value: siteInfo.address, link: null },
    { icon: Phone, title: "Telefono", value: siteInfo.phone, link: `tel:${siteInfo.phone}` },
    { icon: Mail, title: "Email", value: siteInfo.email, link: `mailto:${siteInfo.email}` },
    { icon: Clock, title: "Orari", value: "Lun-Ven: 9:00 - 18:00", link: null }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand text-white overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-30"></div>
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <Badge className="bg-white/10 text-emerald-100 border-white/20 mb-6 backdrop-blur-sm">Contatti</Badge>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6">
            Parliamo del tuo <span className="italic text-emerald-300">futuro</span>
          </h1>
          <p className="text-xl text-emerald-100/90 max-w-3xl mx-auto">
            Hai domande sui corsi? Vuoi maggiori informazioni? Siamo a tua disposizione.
          </p>
        </div>

        <svg className="w-full h-16 -mb-1" viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,60 C480,10 960,10 1440,60 L1440,60 L0,60 Z" fill="#fafaf7" />
        </svg>
      </section>

      {/* Info cards */}
      <section className="py-20 bg-[#fafaf7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {infoCards.map((c, i) => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-emerald-100 hover:border-brand hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded-xl bg-brand-light text-brand flex items-center justify-center mb-4">
                  <c.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xs uppercase tracking-wider font-bold text-brand-dark/60 mb-2">{c.title}</h3>
                {c.link ? (
                  <a href={c.link} className="font-semibold text-brand-dark hover:text-brand">{c.value}</a>
                ) : (
                  <p className="font-semibold text-brand-dark">{c.value}</p>
                )}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-5 gap-10">
            {/* Form */}
            <div className="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 border border-emerald-100 shadow-lg">
              <h2 className="font-display text-3xl font-bold text-brand-dark mb-2">Inviaci un messaggio</h2>
              <p className="text-brand-dark/70 mb-8">Ti risponderemo entro 24 ore.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-semibold text-brand-dark mb-2 block">Nome e cognome *</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-xl border-emerald-100 focus-visible:ring-brand" placeholder="Il tuo nome" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-brand-dark mb-2 block">Email *</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 rounded-xl border-emerald-100 focus-visible:ring-brand" placeholder="la-tua@email.it" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-semibold text-brand-dark mb-2 block">Telefono</Label>
                    <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-xl border-emerald-100 focus-visible:ring-brand" placeholder="+39 ..." />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-brand-dark mb-2 block">Oggetto *</Label>
                    <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 rounded-xl border-emerald-100 focus-visible:ring-brand" placeholder="Info corso OSS" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-brand-dark mb-2 block">Messaggio *</Label>
                  <Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl border-emerald-100 focus-visible:ring-brand resize-none" placeholder="Scrivi il tuo messaggio..." />
                </div>

                <Button type="submit" disabled={loading} className="bg-brand hover:bg-brand-dark text-white rounded-full h-13 px-8 font-bold w-full sm:w-auto py-4">
                  {loading ? "Invio in corso..." : (<>Invia messaggio <Send className="w-4 h-4 ml-2" /></>)}
                </Button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-brand text-white rounded-3xl p-8">
                <h3 className="font-display text-2xl font-bold mb-4">Preferisci chiamarci?</h3>
                <p className="text-emerald-100/90 mb-6 leading-relaxed">I nostri operatori sono a disposizione per fornirti tutte le informazioni sui corsi.</p>
                <a href={`tel:${siteInfo.phone}`} className="block">
                  <div className="bg-white/10 hover:bg-white/20 rounded-2xl p-4 flex items-center gap-3 backdrop-blur-sm">
                    <Phone className="w-6 h-6" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-emerald-200">Chiama ora</div>
                      <div className="font-display font-bold text-xl">{siteInfo.phone}</div>
                    </div>
                  </div>
                </a>
              </div>

              <div className="bg-white rounded-3xl p-8 border border-emerald-100">
                <h3 className="font-display text-xl font-bold text-brand-dark mb-5">Seguici sui social</h3>
                <div className="space-y-3">
                  <a href={siteInfo.socials.whatsapp} className="flex items-center gap-3 p-3 rounded-xl bg-brand-light hover:bg-brand hover:text-white text-brand-dark transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-semibold">WhatsApp</span>
                  </a>
                  <a href={siteInfo.socials.facebook} className="flex items-center gap-3 p-3 rounded-xl bg-brand-light hover:bg-brand hover:text-white text-brand-dark transition-colors">
                    <Facebook className="w-5 h-5" />
                    <span className="font-semibold">Facebook</span>
                  </a>
                  <a href={siteInfo.socials.instagram} className="flex items-center gap-3 p-3 rounded-xl bg-brand-light hover:bg-brand hover:text-white text-brand-dark transition-colors">
                    <Instagram className="w-5 h-5" />
                    <span className="font-semibold">Instagram</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
